import { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ChevronDownIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/base-components';
import {
  DropdownList,
  DropdownListItem,
} from '@rsos/base-components/Dropdown/DropdownComponents';
import {
  setManualLanguage,
  useSetCallerLanguageMutation,
  useGetSupportedLanguagesQuery,
  selectTranscriptionForCall,
} from '../../transcriptionSlice';
import { getLanguageNameByCode } from './supportedLanguages';

const DROPDOWN_CLOSE_DELAY_MS = 200;
const LANGUAGE_CHANGE_COOLDOWN_MS = 2000;

interface ManualLanguageSelectorProps {
  callerId: string;
  disabled?: boolean;
}

const SelectorContainer = styled(Box).attrs({
  horizontal: true,
  align: 'center',
  gap: '12px',
  width: '100%',
})``;

const SelectorLabel = styled(Text).attrs({
  size: 'small',
  color: 'secondaryText',
  fontWeight: 'semibold',
})`
  flex-shrink: 0;
  white-space: nowrap;
  margin-right: 8px;
`;

const SelectorWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  max-width: 140px;
`;

const SelectorButton = styled.button<{
  $isOpen: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  height: 24px;
  width: 100%;
  min-width: 0;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.heavyLineOutline};
  border-radius: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primaryText};
  font-size: 12px;
  transition: all 0.15s ease-in-out;
  justify-content: space-between;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primaryBase};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    transition: transform 0.15s ease-in-out;
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0)')};
    flex-shrink: 0;
  }
`;

const SelectorText = styled.span<{
  $isDetecting: boolean;
}>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  text-align: left;
  font-style: ${({ $isDetecting }) => ($isDetecting ? 'italic' : 'normal')};
`;

const DropdownMenu = styled(DropdownList).attrs({
  role: 'listbox',
  'data-name': 'language-dropdown-menu',
})`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.heavyLineOutline};
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  background: ${({ theme }) => theme.colors.background};
`;

interface RootState {
  emergencyCalls: {
    selectedCallerID: string;
  };
  textToVoice?: {
    calls?: Record<string, { visible?: boolean }>;
  };
}

const ManualLanguageSelector = ({
  callerId,
  disabled = false,
}: ManualLanguageSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isInCooldown, setIsInCooldown] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dispatch = useDispatch();

  const [setCallerLanguage, { isLoading: isSettingLanguage }] =
    useSetCallerLanguageMutation();
  const {
    data: languagesData,
    isLoading: isLoadingLanguages,
    refetch,
  } = useGetSupportedLanguagesQuery(callerId);

  const supportedLanguages = languagesData?.languages ?? [];
  const canEdit = languagesData?.can_edit ?? true;

  const transcription = useSelector(selectTranscriptionForCall(callerId));
  const manualLanguage = transcription?.manualLanguage;
  const tokens = transcription?.payload?.tokens ?? [];
  const hasConversationStarted = tokens.length > 0;
  const detectedLanguages = transcription?.payload?.languagesDetected || [];
  const hasDetectedLanguage =
    detectedLanguages.length > 0 ||
    tokens.some(token => token.detected_language);
  const hasFetchedWithTokens = useRef(false);

  useEffect(() => {
    if (hasConversationStarted && !hasFetchedWithTokens.current) {
      hasFetchedWithTokens.current = true;
      refetch();
    }
  }, [hasConversationStarted, refetch]);
  const audioStatus = transcription?.payload?.audio_data?.status;
  const isCallEnded =
    audioStatus === 'complete' || audioStatus === 'unavailable';

  const isTextToVoiceActive = useSelector(
    (state: RootState) =>
      state.textToVoice?.calls?.[callerId]?.visible ?? false,
  );

  const isDisabled =
    disabled ||
    !canEdit ||
    isSettingLanguage ||
    isInCooldown ||
    isTextToVoiceActive ||
    isCallEnded;

  const getButtonTooltip = useCallback(() => {
    if (isCallEnded) return 'Language selection unavailable - call has ended';
    if (!canEdit)
      return 'You must be the owning organization to select language';
    if (isTextToVoiceActive)
      return 'Language selection disabled during Text-to-Voice';
    if (isSettingLanguage) return 'Setting language...';
    if (isInCooldown) return 'Please wait before changing language again';
    return 'Select transcription language';
  }, [
    isCallEnded,
    canEdit,
    isTextToVoiceActive,
    isSettingLanguage,
    isInCooldown,
  ]);

  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (!isDisabled) {
      setIsOpen(true);
    }
  }, [isDisabled]);

  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, DROPDOWN_CLOSE_DELAY_MS);
  }, []);

  const handleLanguageSelect = useCallback(
    async (languageCode: string) => {
      setIsOpen(false);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }

      if (languageCode === manualLanguage) {
        return;
      }

      try {
        await setCallerLanguage({
          caller_id: callerId,
          language: languageCode,
        }).unwrap();

        dispatch(
          setManualLanguage({
            callerId,
            language: languageCode,
          }),
        );

        setIsInCooldown(true);
        if (cooldownTimeoutRef.current) {
          clearTimeout(cooldownTimeoutRef.current);
        }
        cooldownTimeoutRef.current = setTimeout(() => {
          setIsInCooldown(false);
          cooldownTimeoutRef.current = null;
        }, LANGUAGE_CHANGE_COOLDOWN_MS);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('Failed to set caller language:', error);
        }
      }
    },
    [callerId, manualLanguage, setCallerLanguage, dispatch],
  );

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
      }
    };
  }, []);

  const displayLanguage = (() => {
    if (manualLanguage) {
      return getLanguageNameByCode(manualLanguage, supportedLanguages);
    }

    // Show "Detecting..." only while actively detecting:
    // - Conversation has started (we have tokens)
    // - No language has been detected yet
    // - Call hasn't ended
    if (hasConversationStarted && !hasDetectedLanguage && !isCallEnded) {
      return 'Detecting...';
    }

    // Show auto-detected language with suffix if available
    if (
      hasConversationStarted &&
      hasDetectedLanguage &&
      detectedLanguages.length > 0
    ) {
      const detectedLanguage = detectedLanguages[detectedLanguages.length - 1];
      const languageName = getLanguageNameByCode(
        detectedLanguage,
        supportedLanguages,
      );
      return `${languageName} (auto-detected)`;
    }

    // Default fallback - show before conversation starts or when no detection
    return 'Select Language';
  })();

  return (
    <SelectorContainer data-name="manual-language-selector-container">
      <SelectorLabel>Caller Language:</SelectorLabel>
      <SelectorWrapper
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <SelectorButton
          $isOpen={isOpen}
          disabled={isDisabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          data-name="manual-language-selector"
          data-testid="manual-language-selector-dropdown"
          title={getButtonTooltip()}
        >
          <SelectorText $isDetecting={displayLanguage === 'Detecting...'}>
            {displayLanguage}
          </SelectorText>
          <ChevronDownIcon width={12} height={12} />
        </SelectorButton>

        {isOpen && !isDisabled && (
          <DropdownMenu>
            {isLoadingLanguages ? (
              <DropdownListItem aria-disabled="true">
                <Text size="small" data-name="language-option-loading">
                  Loading...
                </Text>
              </DropdownListItem>
            ) : (
              supportedLanguages.map(language => (
                <DropdownListItem
                  key={language.code}
                  onClick={() => handleLanguageSelect(language.code)}
                  data-testid={`language-option-${language.name}`}
                >
                  <Text
                    size="small"
                    data-name={`language-option-${language.code}`}
                  >
                    {language.name}
                  </Text>
                </DropdownListItem>
              ))
            )}
          </DropdownMenu>
        )}
      </SelectorWrapper>
    </SelectorContainer>
  );
};

export default ManualLanguageSelector;
